import React, { type ReactNode } from 'react';
import MuiDivider from '@mui/material/Divider';
import Box from '../primitives/Box';
import Button from '../actions/Button';
import Stack from '../primitives/Stack';
import { layout, table } from '@/shared/theme/tokens';

export interface FilterPanelProps {
  open: boolean;
  title?: ReactNode;
  children: ReactNode;
  /** Custom footer; when omitted, reset/apply buttons are rendered if handlers are provided. */
  footer?: ReactNode;
  onReset?: () => void;
  onApply?: () => void;
  resetLabel?: string;
  applyLabel?: string;
  /** Expanded width in px (inner column stays fixed so content does not reflow while sliding). */
  width?: number;
  /**
   * `flush` — shares an edge with the adjacent table (list pages).
   * `card` — own border/radius; pair with ListPage `filterPanelSeparated`.
   */
  variant?: 'flush' | 'card';
}

/**
 * Desktop list-page filter column: sticky header + scrollable body + sticky footer.
 * Width animates open/closed so the adjacent table slides with it.
 */
const FilterPanel: React.FC<FilterPanelProps> = ({
  open,
  title = 'Bộ lọc',
  children,
  footer,
  onReset,
  onApply,
  resetLabel = 'Đặt lại',
  applyLabel = 'Áp dụng',
  width = layout.filterPanelWidth,
  variant = 'flush',
}) => {
  const isCard = variant === 'card';
  const resolvedFooter =
    footer ??
    (onReset || onApply ? (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ px: 2, py: 1.5 }}
      >
        {onReset && (
          <Button
            intent="secondary"
            onClick={onReset}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {resetLabel}
          </Button>
        )}
        {onApply && (
          <Button
            intent="primary"
            onClick={onApply}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {applyLabel}
          </Button>
        )}
      </Stack>
    ) : null);

  return (
    <Box
      sx={{
        width: open ? width : 0,
        flexShrink: 0,
        alignSelf: 'stretch',
        minHeight: 0,
        overflow: 'hidden',
        transition: (theme) =>
          theme.transitions.create(isCard ? ['width', 'margin'] : 'width', {
            easing: open
              ? theme.transitions.easing.easeOut
              : theme.transitions.easing.sharp,
            duration: open
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
        ...(isCard && {
          borderRadius: 1,
          mr: open ? 1.5 : 0,
        }),
      }}
    >
      <Box
        sx={{
          width,
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          pointerEvents: open ? 'auto' : 'none',
          ...(isCard
            ? {
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
              }
            : {
                borderRight: 1,
                borderColor: 'divider',
                borderTopLeftRadius: (theme) => theme.shape.borderRadius,
              }),
        }}
        aria-hidden={!open}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            height: table.headerRowHeight,
            maxHeight: table.headerRowHeight,
            boxSizing: 'border-box',
            bgcolor: 'background.paper',
            color: 'text.secondary',
            whiteSpace: 'nowrap',
            fontSize: table.headerFontSize,
            fontWeight: table.headerFontWeight,
            lineHeight: table.headerLineHeight,
            letterSpacing: table.headerLetterSpacing,
            padding: `${table.headerPaddingY} ${table.headerPaddingX}`,
            borderBottom: 1,
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          {title}
        </Box>

        <Stack
          spacing={2.5}
          sx={{
            px: 2,
            py: 2,
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          {children}
        </Stack>

        {resolvedFooter && (
          <>
            <MuiDivider />
            <Box sx={{ flexShrink: 0 }}>{resolvedFooter}</Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default FilterPanel;
